import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Grid, Chip, alpha, IconButton, Divider
} from '@mui/material';
import { IonIcon } from '@ionic/react';
import { close, medkit, cash, calendar } from 'ionicons/icons';
import { Procedure } from '../../../models/procedure';

interface ProcedureDetailModalProps {
    open: boolean;
    onClose: () => void;
    procedure: Procedure | null;
    onEdit: () => void;
}

const ProcedureDetailModal: React.FC<ProcedureDetailModalProps> = ({ open, onClose, procedure, onEdit }) => {
    if (!procedure) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 102, 204, 0.2)'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                color: 'white',
                p: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IonIcon icon={medkit} style={{ fontSize: '2rem' }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {procedure.name}
                            </Typography>
                            <Chip
                                label={procedure.procedure_code}
                                size="small"
                                sx={{
                                    mt: 1,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontFamily: 'monospace'
                                }}
                            />
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <IonIcon icon={close} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                {/* Fee and Date Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                            color: 'white'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IonIcon icon={cash} style={{ fontSize: '2rem' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Base Fee
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        ₦{Number(procedure.base_fee).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            color: 'white'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IonIcon icon={calendar} style={{ fontSize: '2rem' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Created On
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {new Date(procedure.created_at).toLocaleDateString('en-NG', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Description */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                        Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.8 }}>
                        {procedure.description}
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Procedure ID */}
                <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                        Procedure ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', fontFamily: 'monospace' }}>
                        {procedure.geacloud_procedures_id || 'N/A'}
                    </Typography>
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                <Button
                    onClick={onClose}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        color: '#64748b',
                        '&:hover': {
                            bgcolor: '#f1f5f9'
                        }
                    }}
                >
                    Close
                </Button>
                <Button
                    onClick={onEdit}
                    variant="contained"
                    sx={{
                        borderRadius: 2,
                        px: 4,
                        background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                        boxShadow: '0 4px 12px rgba(0, 102, 204, 0.3)',
                        fontWeight: 600,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #0052a3 0%, #003d7a 100%)',
                            boxShadow: '0 6px 16px rgba(0, 102, 204, 0.4)'
                        }
                    }}
                >
                    Edit Procedure
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProcedureDetailModal;
